import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { notification } from 'antd';
import api from 'constants/api';
import Api from 'util/Api';

const unlinkCard = async (trackCode: string): Promise<void> => {
  const response = await Api.apiAxios.delete(api.interbank.link(trackCode));

  if (!Api.isSuccessResponse(response))
    throw new Error(
      'No se ha podido desasignar el código. Intente de nuevo más tarde.',
    );
};

export const useUnlink = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ trackCode }: { trackCode: string }) => unlinkCard(trackCode),
    onSuccess: (_, { trackCode }) => {
      queryClient.invalidateQueries({ queryKey: ['codePack', trackCode] });
      notification.success({
        message: 'Desvinculación exitosa',
        description: 'Se ha desvinculado el código de tarjeta de este envío.',
      });
    },
    onError: (error) => {
      return notification.error({
        message: 'Ocurrio un error',
        description: error.message,
      });
    },
  });
};

export interface LinkedCard {
  requestId: string;
  cardNumber?: string | null;
  cardType: string;
  cardBrand: string;
}

export interface CodePackResponse {
  codPack: string;
  LinkedCards: LinkedCard[];
}

export interface LinkingProposal {
  requestId: string;
  cardNumber: string;
}

export interface LinkCardCodesRequest {
  codPack: string;
  LinkingProposal: LinkingProposal[];
}

export interface LinkingResult {
  linkedCodPack?: string;
  alreadyLinked?: boolean;
  requestId: string;
  cardNumber: string;
  result: 'OK' | 'NOK';
  reason: string;
}

export interface LinkCardCodesResponse {
  codPack: string;
  linkingResults: LinkingResult[];
}

const fetchCodePack = async (
  externalCode: string,
): Promise<CodePackResponse | null> => {
  const response = await Api.apiAxios.get<CodePackResponse>(
    api.interbank.stateByCode(externalCode),
  );
  if (!Api.isSuccessResponse(response)) {
    if (response.status === 400) return null;
    const error = new Error(
      'Ocurrió un error verificando la vinculacion de codigos de solicitud',
    );
    notification.error(error);
    throw error;
  }

  return response.data;
};

const linkCardCodes = async (
  request: LinkCardCodesRequest,
): Promise<LinkCardCodesResponse> => {
  const response = await Api.apiAxios.post<LinkCardCodesResponse>(
    api.interbank.link(request.codPack),
    request,
  );

  if (!Api.isSuccessResponse(response))
    throw new Error(
      'No se ha podido asignar el código. Intente de nuevo más tarde.',
    );
  return response.data;
};

/**
 * Hook para obtener el paquete de códigos asociado a un código externo
 */
export const useGetCodePack = (externalCode: string) => {
  return useQuery({
    queryKey: ['codePack', externalCode],
    queryFn: () => fetchCodePack(externalCode),
    enabled: !!externalCode,
    staleTime: 5 * 60 * 1000, // 5 minutos
    gcTime: 10 * 60 * 1000, // 10 minutos (antes era cacheTime)
  });
};

/**
 * Hook para vincular códigos de tarjeta en lote
 */
export const useLinkCardCodes = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: linkCardCodes,
    onSuccess: (data, variables) => {
      // Invalidar cache del code pack para refrescar datos
      queryClient.invalidateQueries({
        queryKey: ['codePack', variables.codPack],
      });
    },
    onError: (error) => {
      console.error('Error al vincular códigos:', error);
    },
  });
};

// Datos de marcas de tarjetas (Anexo 2)
const cardTypes = [
  { code: '01', name: 'VISA CLASICA', state: 'A' },
  { code: '02', name: 'VISA ORO', state: 'A' },
  { code: '14', name: 'VISA SIGNATURE', state: 'A' },
  { code: '17', name: 'VISA PREMIA', state: 'A' },
  { code: '23', name: 'VISA PLATINUM', state: 'A' },
  { code: '29', name: 'VISA INFINITE', state: 'A' },
  { code: '31', name: 'VISA ACCESS', state: 'A' },
  { code: '03', name: 'AMEX CENTURION GREEN', state: 'A' },
  { code: '04', name: 'AMEX CENTURION GOLD', state: 'A' },
  { code: '06', name: 'AMEX CENTURION PLATINUM', state: 'A' },
  { code: '07', name: 'AMEX BENEFIT', state: 'A' },
  { code: '08', name: 'BBOX AMEX PLATINUM', state: 'A' },
  { code: '09', name: 'BBOX AMEX BLACK', state: 'A' },
  { code: '04', name: 'VISA EMPRESARIAL', state: 'I' },
  { code: '50', name: 'VISA DEB. WHITE', state: 'I' },
  { code: '52', name: 'VISA DEB. BENEFIT', state: 'I' },
  { code: '60', name: 'VISA DEB. CTA NEGOCIOS', state: 'I' },
  { code: '62', name: 'PROPIA PEQUEÑA EMPRESA', state: 'I' },
  { code: '63', name: 'PROPIA EMPRESARIAL TIE', state: 'I' },
  { code: '90', name: 'PROPIA DEB. AGENTE', state: 'I' },
  { code: '16', name: 'VISA CASHBACK SIGNATURE', state: 'I' },
  { code: '19', name: 'VISA RAPPIBANK', state: 'I' },
  { code: '21', name: 'VISA CASHBACK PLATINUM', state: 'I' },
  { code: '22', name: 'VISA CASHBACK ORO', state: 'I' },
  { code: '24', name: 'VEA VISA ORO', state: 'I' },
  { code: '30', name: 'CLARO VISA ORO', state: 'I' },
  { code: '10', name: 'AMEX LIFEMILES GOLD', state: 'I' },
  { code: '11', name: 'AMEX LIFEMILES PLATINUM', state: 'I' },
  { code: '12', name: 'AMEX LIFEMILES ELITE', state: 'I' },
  { code: '01', name: 'MASTERCARD CLASICA', state: 'I' },
  { code: '02', name: 'MASTERCARD ORO', state: 'I' },
  { code: '03', name: 'MASTERCARD EMPRESARIAL', state: 'I' },
  { code: '04', name: 'MASTERCARD PLATINUM', state: 'I' },
];

// Datos de tipos de tarjetas (Anexo 1)
const cardBrands = [
  { code: '01', name: 'VISA', state: 'A' },
  { code: '02', name: 'AMERICAN EXPRESS', state: 'A' },
  { code: '03', name: 'MARCA PRIVADA', state: 'I' },
  { code: '04', name: 'MASTERCARD', state: 'I' },
];

export function getCardBrandByCode(code: string) {
  const codeStr = String(code).padStart(2, '0');
  const brand = cardBrands.find((item) => item.code === codeStr);

  if (!brand) {
    return {
      code,
      name: code,
      state: 'I', // Estado por defecto si no se encuentra la marca
    };
  }

  return {
    code: brand.code,
    name: brand.name,
    state: brand.state,
  };
}

export function getCardTypeByCode(code: string) {
  const codeStr = String(code).padStart(2, '0');
  const type = cardTypes.find((item) => item.code === codeStr);

  if (!type) {
    return {
      code,
      name: code,
      state: 'I', // Estado por defecto si no se encuentra la marca
    };
  }

  return {
    code: type.code,
    name: type.name,
    state: type.state,
  };
}

// Card validation constants and functions
export const CARD_PREFIX_TO_BRAND_CODE: Record<string, string> = {
  PR: '17', // Visa Premia
  CL: '01', // Visa Clasica
  AC: '31', // Visa Access
  OR: '02', // Visa Oro
  PL: '23', // Visa Platinum
  SR: '14', // Visa Signature
  IR: '29', // Visa Infinite
  BB: '09', // BBOX Amex Black
  BP: '08', // BBOX Amex Platinum
  AB: '07',
  BJ: '07', // BBOX Amex Blue Joven
  IF: '29', // Visa Infinite
  SF: '14', // Visa Signature
};
